import {FC, MouseEvent} from 'react';
import {Avatar, Box, Button, ButtonProps, Stack, Typography} from '@mui/material';
import {
    avatarBoxSx,
    avatarSx,
    btnWrapperSx,
    cardSx,
    cardTextReadSx,
    dateSx,
    detailsBoxSx,
    leftColSx,
    nameRowSx,
    nameTextSx,
    newMarkSx,
    readBoxSx,
    readByTextSx,
    rightColSx,
    roleTextSx,
    selectBtnSx,
    textBoxSx,
    unreadSx,
    visibilitySx,
} from './NotificationCard.sx';
import {FormattedMessage, useIntl} from 'react-intl';
import dayjs from 'dayjs';
import {Link} from 'react-router-dom';
import {AppRouteEnum, AppRouteSpace, isErrorObject} from '../../../utils/helper';
import {notificationTypeTranslation, notificationTypeTranslationMessage,} from '../../../utils/consts';
import {NotificationTypeEnum} from '../../../types';
import {Visibility} from '@mui/icons-material';
import {notificationApi} from '../../../api';
import {store} from '../../../app/store';
import {preloaderChangeStatus} from '../../../stores/common/commonSlice';
import {NotifyError} from '../../../utils/notification';
import {useBusinessId, useAppSelector} from '../../../app/hooks';
import {NotificationCardProps} from './NotificationCard.types';
import {UserTypeEnum} from "../../../enums";
import {useCan} from 'src/permissions/hooks';

type MarkAsUnreadType = ReturnType<typeof notificationApi.useMarkNotificationAsUnreadMutation>[0];

export const handleMarkAsUnread = async (
    trigger: MarkAsUnreadType,
    notificationId: number,
    businessId: number,
    notificationType: NotificationTypeEnum
) => {
    try {
        store.dispatch(preloaderChangeStatus(true));
        await trigger({
            notificationId,
            data: {
                businessId,
                notificationType: notificationType,
            },
        }).unwrap();
    } catch (error) {
        if (isErrorObject(error)) {
            NotifyError(error.data?.message ?? error.data?.error);
        }
    } finally {
        store.dispatch(preloaderChangeStatus(false));
    }
};

export const NotificationCard: FC<NotificationCardProps> = ({
                                                                id,
                                                                read,
                                                                user,
                                                                created,
                                                                type,
                                                                message,
                                                                readAt,
                                                                readByManager,
                                                                page,
                                                                location,
                                                                isSelected,
                                                                onSelectChange,
                                                            }) => {
    const intl = useIntl();
    // The caller's business, not the notification's. `NotificationDTO` claimed to carry a `businessId`
    // and the server has never sent one - it sends a `business` object - so this arrived undefined,
    // axios dropped the parameter, and marking as unread answered "Required request parameter
    // 'businessId' is not present". Marking as read worked only because its two callers ask the hook.
    const businessId = useBusinessId();
    const [setAsUnread] = notificationApi.useMarkNotificationAsUnreadMutation();
    // The same permission the list above uses to offer "mark as read".
    const canMarkAsUnread = useCan('notifications.update');
    const {authInfo} = useAppSelector((state) => state.common);

    const handleSelectClick = (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        onSelectChange?.(id, !isSelected);
    };

    const isCompanyUser = user
        ? user.userType === UserTypeEnum.COMPANY
        : false;

    let userName = '';

    if (type === NotificationTypeEnum.NEW_COMPANY_REGISTRATION_REQUEST) {
        try {
            const data = JSON.parse(message || '{}');
            userName = data.companyName ?? 'Company';
        } catch {
            userName = 'Company';
        }
    } else if (user) {
        if (isCompanyUser) {
            userName = user.company?.name ?? 'Company User';
        } else {
            // For drivers, use firstName lastName or fallback to emailAddress
            if (user.firstName && user.lastName) {
                userName = `${user.firstName} ${user.lastName}`;
            } else {
                userName = user.emailAddress ?? 'User';
            }
        }
    } else if (location) {
        userName = location.name;
    } else {
        userName = 'User';
    }

    const getCompanyAddress = (): string => {
        try {
            const data = JSON.parse(message || '{}');
            return `${data.parkingAddress?.country}, ${data.parkingAddress?.city}, ${data.parkingAddress?.street}, ${data.parkingAddress?.houseNumber}`;
        } catch {
            return 'Address not available';
        }
    };

    const handleUnreadClick: ButtonProps['onClick'] = (e) => {
        e.preventDefault();
        void handleMarkAsUnread(setAsUnread, Number(id), businessId, type);
    };

    const description = (() => {
        if (type === NotificationTypeEnum.CHANGE_PLAN_REQUEST || type === NotificationTypeEnum.USER_FEEDBACK) {
            return notificationTypeTranslationMessage(intl, {
                id, read, user, created, type, message, readAt, readByManager, location,
            })[type];
        }
        if (type === NotificationTypeEnum.NEW_COMPANY_REGISTRATION_REQUEST) {
            return getCompanyAddress();
        }
        if (user) {
            return [user?.parkingAddress?.country, user?.parkingAddress?.city, user?.parkingAddress?.street, user?.parkingAddress?.houseNumber].filter(Boolean).join(', ');
        }
        if (location) {
            return [location?.address?.country, location?.address?.city, location?.address?.street, location?.address?.houseNumber].filter(Boolean).join(', ');
        }
        return '';
    })();

    return (
        <Link
            to={{
                pathname: AppRouteSpace([AppRouteEnum.app, AppRouteEnum.notifications, String(id)]),
                search: `?type=${type}`,
            }}
            state={{page}}
            style={{textDecoration: 'none'}}
        >
            <Box sx={cardSx(read, isSelected)}>
                <Box sx={avatarBoxSx}>
                    {!read && <Box sx={newMarkSx}/>}
                    <Avatar
                        src={user?.avatar?.url}
                        sx={avatarSx}
                    />
                </Box>
                <Stack sx={textBoxSx}>
                    <Box sx={leftColSx}>
                        <Box sx={nameRowSx}>
                            <Typography
                                component="span"
                                sx={nameTextSx(read)}
                            >
                                {userName}
                            </Typography>
                            <Typography
                                sx={roleTextSx}
                                component="span"
                            >
                                {notificationTypeTranslation(intl)[type]}
                            </Typography>
                        </Box>
                        {description && (
                            <Typography
                                component="span"
                                sx={cardTextReadSx(read)}
                            >
                                {description}
                            </Typography>
                        )}
                    </Box>
                    <Box sx={rightColSx}>
                        <Stack sx={detailsBoxSx}>
                            <Typography sx={dateSx}>
                                {dayjs(created).format('DD.MM.YYYY HH:mm')}
                            </Typography>
                        </Stack>
                        {Boolean(readByManager && readAt && read) && (
                            <Box sx={readBoxSx}>
                                <Visibility sx={visibilitySx}/>
                                <Typography sx={readByTextSx}>
                                    <FormattedMessage
                                        id="readByOn"
                                        values={{
                                            name: `${readByManager?.firstName} ${readByManager?.lastName}`,
                                            date: dayjs(readAt).format('DD.MM.YYYY HH:mm'),
                                        }}
                                    />
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Stack>
                <Box sx={btnWrapperSx}>
                    {canMarkAsUnread && (
                        <Button
                            className="markAsUnread-btn"
                            sx={unreadSx}
                            onClick={handleUnreadClick}
                        >
                            <FormattedMessage id="markAsUnread"/>
                        </Button>
                    )}
                    {onSelectChange && (
                        <Button
                            className="select-btn"
                            sx={selectBtnSx}
                            onClick={handleSelectClick}
                        >
                            <FormattedMessage id={isSelected ? 'removeFromMarkAsRead' : 'addToMarkAsRead'}/>
                        </Button>
                    )}
                </Box>
            </Box>
        </Link>
    );
};
